import type { OpaqueVideoData } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import { IconRegistry } from './registries/IconRegistry';
import { MediaRegistry } from './registries/MediaRegistry';
import { MusicRegistry } from './registries/MusicRegistry';

import {
    getSceneDescriptions,
    getTemplateDescription,
    getVideoDescription,
    getVideoMusicDescription,
} from 'js/src/agent/luminaryUtils';
import { convertJsonToYaml } from 'js/src/agent/utils';
import type { MediaCandidates } from 'js/src/client';

/**
 * Generates a text-based video description.
 * @param luminary The Luminary instance containing video data
 * @param mediaCandidates Optional media candidates to add to registries
 */
export async function generateVideoDescription(
    luminary: Luminary,
    mediaCandidates?: MediaCandidates | null,
): Promise<string> {
    // Ensure all assets from the video are in the registry
    const mediaRegistry = MediaRegistry.getInstance();
    await mediaRegistry.addAnyUnseenItemsFromVideo(luminary);
    const iconRegistry = IconRegistry.getInstance();
    await iconRegistry.addAnyUnseenItemsFromVideo(luminary);
    const musicRegistry = MusicRegistry.getInstance();
    await musicRegistry.addAnyUnseenItemsFromVideo(luminary);

    // Add media candidates from recommendations to registries
    if (mediaCandidates) {
        await mediaRegistry.addCandidatesFromRecommendations(mediaCandidates);
        await iconRegistry.addCandidatesFromRecommendations(mediaCandidates);
    }

    // Overall video description
    let description = '## Video\n';
    description += convertJsonToYaml(getVideoDescription(luminary));
    description += '\n\n## Template\n';
    description += convertJsonToYaml(getTemplateDescription(luminary));
    description += '\n\n## Music\n';
    description += convertJsonToYaml(getVideoMusicDescription(luminary));
    description += '\n\n## Scenes';

    // Per scene description
    for (const scene of luminary.scenes) {
        if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
            // Skip brand scenes
            continue;
        }
        description += `\nSCENE ${scene.globalIndex}\n${convertJsonToYaml(getSceneDescriptions(scene))}\n`;
    }

    return description;
}

/**
 * Generates a comprehensive video description from video data by creating a temporary Luminary instance.
 * This is useful for analyzing videos without having an existing Luminary instance.
 * @param videoData The video data to analyze
 * @returns A formatted string description of the video including metadata, music, and scene details
 */
export async function generateVideoDescriptionFromVideoData(
    videoData: OpaqueVideoData,
): Promise<string> {
    // Create temporary Luminary instance for analysis
    const localLuminary = new Luminary({
        fetchResources: true,
        disableRenderer: true,
        autoDraw: true,
        autoUpdate: true,
    });

    try {
        localLuminary.load(videoData);
        await localLuminary.onAssetsLoaded();

        return await generateVideoDescription(localLuminary);
    } finally {
        // Always clean up the local Luminary instance
        localLuminary.destroy();
    }
}
